// src/views/Seguridad/Usuario/EliminarUsuario.jsx
import React, { useState } from "react";
import { FaArrowLeft } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";

/**
 * Página para confirmar eliminación de un usuario.
 * - Mismo diseño que EliminarRoles, pero con alerta "roja".
 *
 * Props:
 *  - initial: { id, codigo, nombre, estado?, asignadoA?: string[] }
 *  - onVolver(): volver a la lista
 *  - onEliminar(): se llama sólo si el backend eliminó con éxito
 */
export default function EliminarUsuario({
  initial = {
    id: null,
    codigo: "E0000001",
    nombre: "Usuario Demo",
    estado: "ACTIVO",
    asignadoA: [],
  },
  onVolver = () => {},
  onEliminar = () => {},
}) {
  const asignados = Array.isArray(initial?.asignadoA) ? initial.asignadoA : [];
  const count = asignados.length;

  const [removing, setRemoving] = useState(false);
  const [error, setError] = useState("");

  const handleEliminar = async () => {
    if (!initial?.id) return;

    setError("");
    setRemoving(true);
    try {
      const url = apiRoutes.usuarios.deleteUrl
        ? apiRoutes.usuarios.deleteUrl(initial.id)
        : `${apiRoutes?.usuarios?.base ?? ""}/usuarios/${encodeURIComponent(initial.id)}`;

      // Espera un 204 sin body
      await appFetchJson(url, { method: "DELETE" });

      // Si todo OK, avisamos al padre
      await Promise.resolve(onEliminar());
    } catch (e) {
      const status = e?.status ?? e?.response?.status;
      if (status === 404) {
        setError("Usuario no encontrado (puede que ya haya sido eliminado).");
      } else if (status === 409) {
        // Por si en el futuro bloqueamos la eliminación en algún estado
        setError(e?.message || "No se pudo eliminar el usuario (en uso).");
      } else {
        setError(e?.message || "No se pudo eliminar el usuario.");
      }
      setRemoving(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-panel-sm bg-white rounded shadow-lg">

        {/* Header */}
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Eliminar Usuario</h2>
          <button onClick={onVolver} className="modal-close-x">✕</button>
        </div>

        {/* Cuerpo */}
        <div className="modal-body space-y-4">

          <div className="space-y-2">
            <div className="form-row gap-2 items-center">
              <div className="form-label">Código</div>
              <div className="value font-medium">{initial?.codigo ?? "—"}</div>
            </div>

            <div className="form-row gap-2 items-center">
              <div className="form-label">Nombre</div>
              <div className="value">{initial?.nombre ?? "—"}</div>
            </div>

            {initial?.estado && (
              <div className="form-row gap-2 items-center">
                <div className="form-label">Estado</div>
                <div className="value">{initial?.estado}</div>
              </div>
            )}
          </div>

          {/* Mensaje según asignaciones */}
          {count === 0 ? (
            <div className="text-sm text-red-700">
              Este usuario no tiene asignaciones. ¿Deseás eliminarlo de forma permanente?
            </div>
          ) : (
            <div className="space-y-2">
              <div className="text-sm text-red-700">
                <b>Atención:</b> este usuario posee <b>{count}</b> asignación
                {count !== 1 ? "es" : ""}. Al eliminarlo, se quitarán.
              </div>

              <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                <div className="font-medium mb-1">Asignaciones</div>

                {asignados.length === 0 ? (
                  <div>—</div>
                ) : (
                  <ul className="list-disc ml-5 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
                    {asignados.slice(0, 10).map((x) => (
                      <li key={x}>{x}</li>
                    ))}
                  </ul>
                )}

                {asignados.length > 10 && (
                  <div className="mt-1 text-xs opacity-80">
                    y {asignados.length - 10} más…
                  </div>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="text-sm text-red-700">{error}</div>
          )}

          {/* Botones */}
          <div className="mt-4 flex justify-end gap-2">
            <button
              className="btn-fil bg-green-600 text-white hover:bg-green-700"
              onClick={handleEliminar}
              disabled={removing}
            >
              {removing ? "Eliminando…" : "Eliminar"}
            </button>

            <button
              className="btn-fil bg-gray-300 hover:bg-gray-400"
              onClick={onVolver}
            >
              Cancelar
            </button>
          </div>

        </div>
      </div>
    </div>
  );

}